import React, { useState, useEffect, useContext, useRef } from 'react';
import styled from 'styled-components/macro';
import TextareaAutosize from 'react-textarea-autosize';

import { fontDefaultAlt, fontkoKR } from '@mirinae/react-ui';
import { FormattedKoPhrase } from '@mirinae/lessons/components/lesson/elements/Phrase';
import Element from '@mirinae/lessons/components/lesson/elements/Element';
import { os } from '@mirinae/js-utils/utils/userAgent';
import { classes } from '@mirinae/react-ui';

import SuccessOverlay from '@mirinae/lessons/components/lesson/elements/shared/SuccessOverlay';
import GTM from '@mirinae/apis/GoogleAnalytics';
import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import { ClearInput } from '@explorer/components/exploration/input/MultiLineInput';
import ClearButton from '@explorer/components/exploration/input/ClearButton';

const AdvancedTypingElement = styled(Element)`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: start;
`;

const AnswerStage = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-wrap: wrap;
    margin: ${os.isOthers ? 20 : 38}px 0 0px 0;
`;

const CheckButton = styled.button`
    width: 160px;
    height: 50px;
    flex-shrink: 0;
    border-radius: 30px;
    background: ${props => (props.readyForCheck ? '#A5D6D3' : '#DFDFDF')};
    color: #FFF;
    border: none;
    font-size: 17px;
    font-family: ${fontDefaultAlt};
    font-weight: 700;
    margin-top: 20px;
    
    &:hover {
        background: ${props => (props.readyForCheck ? '#48A9A6' : '#DFDFDF')}
    }
`;

const DontKnowButton = styled.button`
    font-family: ${fontDefaultAlt};
    background: none;
    color: #A5D6D3;
    font-size: 14px;
    font-weight: 700;
    margin-top: 15px;
    border: none;
`;

const WordsToUse = styled.div`
    min-width: 540px;
    min-height: 70px;
    padding: 23px;
    border-radius: 15px;
    border: 1px solid var(--gray-c-4-c-4-c-4, #C4C4C4);
    text-align: center;
    color: var(--gray-525252, #525252);
    font-family: ${fontkoKR};
    font-size: 20px;
    font-weight: 500;
    line-height: 120%; /* 24px */
    margin-bottom: 35px;
    
    .${classes.mobile} & {
        min-width: calc(100vw - 40px);
        max-width: calc(100vw - 40px);
        font-size: 20px;
    }
`;

const AnswerInput = styled(TextareaAutosize)`
    position: relative;
    min-width: 620px;
    color: var(--primary-3-c-3-c-3-b-black, #3C3C3B);
    font-family: ${fontkoKR};
    font-size: 22px;
    font-weight: 500;
    line-height: 30px; /* 136.364% */
    text-align: center;
    padding-bottom: 9px;
    border: none;
    border-bottom: solid thin ${props => (props.checkCount >= 1 ? '#FF8826' : '#C4C4C4')};
    
    &::placeholder {
        color: var(--gray-dfdfdf, #DFDFDF);
        font-size: 22px;
        font-weight: 500;
    }

    .${classes.mobile} & {
        min-width: calc(100vw - 40px);
        max-width: calc(100vw - 40px);
    }
`;

const EnglishTranslation = styled.div`
    width: 100%;
    color: var(--gray-7-a-7-a-7-a, #7A7A7A);
    text-align: center;
    font-family: Montserrat;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px; /* 125% */
    margin: 15px 0 15px 0;
`;

const InputWrapper = styled.div`
    position: relative;
`;

const InputAndAnswer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const Input = styled.div`
    font-family: ${fontkoKR};
    font-size: 22px;
    font-weight: 500;
    padding-bottom: 9px;
    min-width: 620px;
    text-align: center;
    border-bottom: thin solid #C4C4C4;
    
    .${classes.mobile} & {
        min-width: calc(100vw - 40px);
        max-width: calc(100vw - 40px);
    }
`;

const TryAgainButton = styled(CheckButton)`
    border-radius: 30px;
    border: 1px solid var(--contents-a-5-d-6-d-3-start, #A5D6D3);
    background: var(--primary-ffffff-white, #FFF);
    color: var(--contents-a-5-d-6-d-3-start, #A5D6D3);
    text-align: center;
    font-family: Montserrat Alternates;
    font-size: 17px;
    font-weight: 700;
    line-height: 20px; /* 117.647% */
    
    
    &:hover {
        border: 1px solid #48A9A6;
        color: #48A9A6;
    }
`;

const answerSentenceStyle = { koFontWeight: '700', koreanFontSize: '30px', align: 'center' };

const AdvancedTyping = ({ element }) => {
    const player = useContext(PlayerContext);
    const [input, setInput] = useState('');
    const [complete, setComplete] = useState(false);
    const [readyForCheck, setReadyForCheck] = useState(false);
    const [wrongAnswer, setWrongAnswer] = useState('');
    const [checkCount, setCheckCount] = useState(0);
    const answerRef = useRef(null);

    const answer = element.fieldsByName.correctAnswer.parts.koreanParts.reduce((prev, curr) => (`${prev.value || prev}${curr.value}`), '').trim().replace(/\.$/, '');

    const check = (e) => {
        // if (answerRef.current.value.trim().replace(/\.$/, '') === answer) {
        // decide to accept any attempt as good, just show our recommended answer
        if (true) {
            setInput(answerRef.current.value);
            setComplete(true);
            player.postEvent({ action: 'complete' }, element);
            GTM.userAction('success', { page: player.currentPage.componentName }, 'lessons');
        } else {
            player.postEvent({ action: 'error' }, element);
            setCheckCount(checkCount + 1);
            setWrongAnswer(answerRef.current.value);
        }
    }

    const inputChanged = (e) => {
        setReadyForCheck(e.target.value !== '');
        setInput(e.target.value);
    }

    useEffect(() => {
        if (answerRef.current) {
            answerRef.current.focus();
        }
    }, []);

    return (
        <AdvancedTypingElement element={element}>
            { complete ? <SuccessOverlay top={120} height={800} left={1} /> : null }
            <WordsToUse>{element.fieldsByName.words.value}</WordsToUse>
            { !complete && checkCount < 2 ? (
                <>
                    <InputWrapper>
                        <AnswerInput autoFocus value={input} checkCount={checkCount.current} onChange={inputChanged} ref={answerRef} />
                        { input.length !== 0 && <ClearButton className="btn-clear" onClick={() => setInput('')} /> }
                    </InputWrapper>
                    <EnglishTranslation>{element.fieldsByName.english.value}</EnglishTranslation>
                </>
            ) : (
                <InputAndAnswer>
                    <Input>{input}</Input>
                    <EnglishTranslation>{element.fieldsByName.english.value}</EnglishTranslation>
                    <FormattedKoPhrase phrase={element.fieldsByName.correctAnswer} phraseStyle={answerSentenceStyle} />
                </InputAndAnswer>
            )}
            { !complete && checkCount !== 2 && <CheckButton disabled={!readyForCheck} readyForCheck={readyForCheck} onClick={check}>Check</CheckButton> }
            { checkCount === 2 && <TryAgainButton onClick={() => { setCheckCount(0); setInput(''); }}>Try again</TryAgainButton> }
            { checkCount === 1 && <DontKnowButton onClick={() => setCheckCount(2)}>I don&apos;t know</DontKnowButton> }

        </AdvancedTypingElement>
    );
};

export default AdvancedTyping;
